import { message } from 'antd'
import { DiscountModels } from '@getgreenline/products'
import { IPaymentQueueLine } from '@getgreenline/homi-shared'
import { Payments } from '@getgreenline/payments'
import { parseErrorMsg } from './helpers'

type CartDiscountLine = Payments.CreatePaymentV2['cartDiscountLines'][number]

type RewardQueueLine = IPaymentQueueLine & {
  externalReward: NonNullable<IPaymentQueueLine['externalReward']>
}

const hasReservation = (queueLine: IPaymentQueueLine): queueLine is RewardQueueLine =>
  !queueLine.productId && !!queueLine.externalReward?.reservationId

const findReferenceDiscount = (
  payment: Payments.CreatePaymentV2,
  discountId: number | null,
): DiscountModels.IDiscount | undefined =>
  discountId === null ? undefined : payment.discounts.find((discount) => discount.id === discountId)

/**
 * Mirrors `CreatePayment.addCartDiscountFromQueue`, which is private to homi-shared: the
 * reference discount is the source of truth for the amount, and the queue line only fills in
 * for a discount that no longer exists.
 */
const addCartDiscountFromQueueLine = (
  payment: Payments.CreatePaymentV2,
  queueLine: IPaymentQueueLine,
): CartDiscountLine => {
  const referenceDiscount = findReferenceDiscount(payment, queueLine.discountId)
  const discountName = referenceDiscount ? referenceDiscount.name : queueLine.productName

  if (queueLine.discountPercentage === 0) {
    const centValue = referenceDiscount
      ? Math.abs(referenceDiscount.amount) * -1
      : queueLine.discountPrice

    return payment.addCartFixedDiscount(centValue, queueLine.discountId, discountName)
  }

  const percent = referenceDiscount ? referenceDiscount.amount : queueLine.discountPercentage

  return payment.addCartPercentDiscount(percent, queueLine.discountId, discountName)
}

/**
 * `CreatePayment.populateFromQueue` treats a cart discount queue line as a cart discount only
 * when its `discountPrice` is truthy, so a reward worth $0 — one clamped to nothing because
 * earlier discounts already covered the whole cart — is silently skipped on unpark. That loses
 * the reward from the sale AND orphans its reservation in Growth, since nothing is left holding
 * the reservation id to release it. Re-add whatever the queue still knows about.
 */
export const restoreDroppedRewardCartDiscounts = (
  payment: Payments.CreatePaymentV2,
  queueLines: IPaymentQueueLine[] = [],
): void => {
  const rewardQueueLines = queueLines.filter(hasReservation)

  if (rewardQueueLines.length === 0) {
    return
  }

  const restoredReservationIds = new Set(
    payment.cartDiscountLines
      .map((line) => line.externalReward?.reservationId)
      .filter((reservationId): reservationId is string => !!reservationId),
  )

  rewardQueueLines.forEach((queueLine) => {
    const { reservationId } = queueLine.externalReward

    if (restoredReservationIds.has(reservationId)) {
      return
    }

    try {
      addCartDiscountFromQueueLine(payment, queueLine).setExternalReward(queueLine.externalReward)
      restoredReservationIds.add(reservationId)
    } catch (error) {
      message.error(
        parseErrorMsg(error) ||
          `Failed to restore the reward "${queueLine.productName}" from this parked sale`,
      )
    }
  })
}
